import { get, set, clear } from "idb-keyval"
import { LiveStream, SeriesStream, VodStream } from "./XtremeCodesAPI.types"

export const localStorageSet = (key: string, data: string): Promise<void> => {
  return set(key, data)
}

export const localStorageGet = (
  key: string,
): Promise<string | null | undefined> => {
  return get(key)
}

export const deleteAccountFromLocalStorage = (): Promise<void> => {
  return clear()
}

export const getDateForTimestamp = (seconds: number): Date => {
  return new Date(seconds * 1000)
}

export const isVod = (
  stream: VodStream | SeriesStream | LiveStream,
): stream is VodStream => {
  const vodStream = stream as VodStream
  return vodStream.stream_id !== undefined && vodStream.stream_type !== "live"
}

export const isSeries = (
  stream: VodStream | SeriesStream | LiveStream,
): stream is SeriesStream => {
  return (stream as SeriesStream).series_id !== undefined
}

export const isLive = (
  stream: VodStream | SeriesStream | LiveStream,
): stream is LiveStream => {
  const liveStream = stream as LiveStream
  return liveStream.stream_type === "live" && liveStream.stream_id !== undefined
}

export const streamBelongsToCategory = (
  stream: { category_id?: number | string; category_ids?: (number | string)[] },
  categoryId: number | string | undefined,
): boolean => {
  if (categoryId === undefined) return false
  if (stream.category_id === categoryId) return true
  return stream.category_ids?.some((id) => id === categoryId) ?? false
}

export const containerToMimeType = (container: string): string => {
  switch (container) {
    case "mkv":
      return "video/webm"
    case "mp4":
    case "mov":
    case "m4v":
      return "video/mp4"
    case "avi":
      return "video/x-msvideo"
    case "3gp":
      return "video/3gpp"
    case "mpg":
    case "mpeg":
      return "video/mpeg"
    case "ogg":
    case "ogv":
    case "opus":
      return "video/ogg"
    case "mpd":
      return "application/dash+xml"
    case "m3u8":
      return "application/x-mpegURL"
    default:
      return `video/${container}`
  }
}

export const copyTextToClibpboard = async (text: string) => {
  if (navigator.clipboard) {
    await navigator.clipboard.writeText(text)
  } // todo: fix bc this shit wont work on http site
  else {
    unsecuredCopyToClipboard(text)
  }
  console.log(text)
}

function unsecuredCopyToClipboard(text: string) {
  const textArea = document.createElement("textarea")
  textArea.value = text
  document.body.appendChild(textArea)
  textArea.focus()
  textArea.select()
  try {
    document.execCommand("copy")
  } catch (err) {
    console.error("Unable to copy to clipboard", err)
  }
  document.body.removeChild(textArea)
}

export const b64DecodeUnicode = (str: string) => {
  // Going backwards: from bytestream, to percent-encoding, to original string.
  return decodeURIComponent(atob(str).split('').map(function(c) {
    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
  }).join(''));
}

const getUserAgent = (): string => {
  if (typeof navigator === "undefined") return ""
  return navigator.userAgent || ""
}

export const isAndroid = (): boolean => /android/i.test(getUserAgent())

export const isIos = (): boolean => {
  const ua = getUserAgent()
  if (/iPad|iPhone|iPod/i.test(ua)) return true
  if (typeof window === "undefined") return false
  // iPadOS reports Macintosh , touch points tell it apart from a real Mac.
  const nav = navigator as Navigator & {
    userAgentData?: { platform?: string }
    platform?: string
  }
  const platform = nav.userAgentData?.platform ?? nav.platform ?? ""
  return /mac/i.test(platform) && navigator.maxTouchPoints > 1
}

// VLC only registers its URL handler on Android and iOS , desktop builds
// don't handle vlc:// links, so the "Open in VLC" button is gated to these.
export const isVlcPlatform = (): boolean => isAndroid() || isIos()

export const buildVlcUrl = (mediaUrl: string): string => {
  // Per https://wiki.videolan.org/Documentation:IOS/ :
  // vlc-x-callback://x-callback-url/ACTION?url=... , `stream` plays the
  // stream from the URL parameter (x-success/x-error optional). The inner
  // URL is percent-encoded per the x-callback-url spec.
  if (isIos()) {
    return `vlc-x-callback://x-callback-url/stream?url=${encodeURIComponent(mediaUrl)}`
  }
  return `vlc://${mediaUrl}`
}

export const openInVlc = (mediaUrl: string): void => {
  // Must run in the button's tap/click handler so the OS honors the scheme.
  window.location.href = buildVlcUrl(mediaUrl)
}
